import React, { useState } from 'react';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import { Sprite } from '../Sprites/Sprite';
import type { SpriteName } from '../Sprites/Sprite';
import './CompoundInterest.scss';

/**
 * 「世界的第八大奇蹟是複息」, an in-house replacement for the Piktochart of the same name, in the look of
 * ai.portfoplus.com. As in the Piktochart, $100 put in once is a pink piggy bank, and every year's interest is
 * another piggy stacked on top, a little fatter each year because it is earned on the whole pile, so the stack
 * grows faster and faster. A rate chip and a year slider let the adviser try other cases; the button at the foot
 * opens the same compounding over the longer terms of a policy.
 */

const PRINCIPAL = 100;
const RATES = [5, 6.5, 8];
const DEFAULT_RATE = 6.5;
const MAX_YEARS = 30;
const DEFAULT_YEARS = 20;
/** The moments the Piktochart draws, as years after the deposit. */
const BAR_YEARS = [0, 1, 2, 3, 10];
/** The button compares starting at 25 and at 35, both left to grow until 65. */
const START_AGES = [25, 35];
const END_AGE = 65;
/** The interest piggies, a new colour each year as in the Piktochart (generated sprites). */
const PIG_SPRITES: SpriteName[] = [
  'pig-grey',
  'pig-green',
  'pig-blue',
  'pig-purple',
  'pig-red',
  'pig-yellow',
  'pig-orange',
  'pig-teal'
];

/** What the deposit is worth after `years` years of `rate`% on the whole balance. */
export const balance = (years: number, rate = DEFAULT_RATE) => PRINCIPAL * Math.pow(1 + rate / 100, years);

/** The interest earned in year `year` alone: it grows each year, being paid on last year's interest too. */
export const yearInterest = (year: number, rate = DEFAULT_RATE) => balance(year, rate) - balance(year - 1, rate);

/** "187.7", "121" or "1,744.9": one decimal at most, none when it is whole. */
export const money = (value: number) =>
  (Math.round(value * 10) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 });

/** A piggy bank, facing right, with an optional label on its side. */
const Pig = ({ name, width, children }: { name: SpriteName; width?: number; children?: React.ReactNode }) => (
  <div className="cmi-pig" style={width ? { width } : undefined}>
    <Sprite name={name} />
    {children && <span className="cmi-pig-text">{children}</span>}
  </div>
);

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const CompoundInterest = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [rate, setRate] = useState<number>(DEFAULT_RATE);
  const [years, setYears] = useState<number>(DEFAULT_YEARS);
  const [onPolicy, setOnPolicy] = useState<boolean>(false);

  const total = balance(years, rate);
  /** "1.9": how many times the deposit the balance is. */
  const times = (value: number) => (Math.round((value / PRINCIPAL) * 10) / 10).toString();
  const yearLabel = (y: number) => (y === 0 ? t('cmi-start', { p: PRINCIPAL }) : t('cmi-year', { y }));
  /** An interest piggy's width: not to scale (it would be a speck), but fatter as the interest grows. */
  const pigWidth = (interest: number) => Math.round(30 + interest * 3.2);

  return (
    <div className="compound-interest" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="cmi-head">
        <h2 className="cmi-title">
          <FormatQuoteRoundedIcon aria-hidden="true" />
          <span>{t('cmi-quote')}</span>
        </h2>
        <p className="cmi-by">{t('cmi-by')}</p>
        <p className="cmi-def">
          <span>{t('cmi-def-1')}</span>
          {t('cmi-def-2')}
          <strong>{t('cmi-def-key')}</strong>
        </p>
        <p className="cmi-assume">{t('cmi-assume', { p: PRINCIPAL, rate })}</p>
      </header>

      <div className="cmi-rates" role="group" aria-label={t('cmi-rate-label')}>
        <span>{t('cmi-rate-label')}</span>
        {RATES.map(r => (
          <button
            key={r}
            type="button"
            className={r === rate ? 'is-on' : ''}
            aria-pressed={r === rate}
            onClick={() => setRate(r)}
          >
            {r}%
          </button>
        ))}
      </div>

      {/* Keyed on the rate, so the piggies drop in again when it changes. */}
      <figure key={rate} className="cmi-chart" role="img" aria-label={t('cmi-chart-label')}>
        <div className="cmi-plot">
          {BAR_YEARS.map((y, col) => (
            <div key={y} className="cmi-col">
              <div className="cmi-total">
                <span>{t('cmi-balance')}</span>
                <strong>${money(balance(y, rate))}</strong>
              </div>
              <div className="cmi-stack">
                {Array.from({ length: y }, (_, k) => y - k).map(year => (
                  <div
                    key={year}
                    className="cmi-drop"
                    style={{ '--d': `${col * 0.15 + year * 0.07}s` } as React.CSSProperties}
                  >
                    <Pig name={PIG_SPRITES[(year - 1) % PIG_SPRITES.length]} width={pigWidth(yearInterest(year, rate))}>
                      {y === 1 ? t('cmi-interest') : null}
                    </Pig>
                  </div>
                ))}
                <Pig name="pig-pink" width={84}>
                  <b>${PRINCIPAL}</b>
                  {t('cmi-deposit')}
                </Pig>
              </div>
            </div>
          ))}
        </div>
        <div className="cmi-labels">
          {BAR_YEARS.map(y => (
            <span key={y}>{yearLabel(y)}</span>
          ))}
        </div>
      </figure>

      <div className="cmi-banner">
        <p>{t('cmi-banner', { total: money(balance(10, rate)), times: times(balance(10, rate)) })}</p>
      </div>

      <section className="cmi-try" aria-live="polite">
        <label htmlFor="cmi-years">
          <span>{t('cmi-try')}</span>
          <strong>{t('cmi-years', { y: years })}</strong>
        </label>
        <input
          id="cmi-years"
          type="range"
          min={0}
          max={MAX_YEARS}
          value={years}
          onChange={event => setYears(Number(event.target.value))}
          style={{ '--fill': `${(years / MAX_YEARS) * 100}%` } as React.CSSProperties}
        />
        {/* One small piggy a year, each a little fatter than the one before. */}
        <div className="cmi-herd" aria-hidden="true">
          {Array.from({ length: years }, (_, k) => k + 1).map(year => (
            <span key={year} className="cmi-mini" style={{ width: `${10 + yearInterest(year, rate) * 0.9}px` }}>
              <Pig name={PIG_SPRITES[(year - 1) % PIG_SPRITES.length]} />
            </span>
          ))}
        </div>
        <p className="cmi-result">
          {t('cmi-result', {
            y: years,
            total: `$${money(total)}`,
            times: times(total),
            interest: `$${money(total - PRINCIPAL)}`
          })}
        </p>
      </section>

      <div className="cmi-policy">
        <button
          type="button"
          className="cmi-policy-btn"
          aria-expanded={onPolicy}
          onClick={() => setOnPolicy(!onPolicy)}
        >
          <TouchAppRoundedIcon aria-hidden="true" />
          {t('cmi-policy-btn')}
        </button>
        {onPolicy && (
          <div className="cmi-policy-body">
            <p className="cmi-policy-key">{t('cmi-policy-key')}</p>
            <ul>
              {START_AGES.map((age, i) => {
                const y = END_AGE - age;
                return (
                  <li key={age}>
                    <span className="cmi-age">{t('cmi-age-start', { age })}</span>
                    {/* The years the money rolls over, to scale, with the piggy it grows into at 65. */}
                    <div className="cmi-track">
                      <div className="cmi-track-fill" style={{ width: `${(y / (END_AGE - START_AGES[0])) * 100}%` }}>
                        <span>{t('cmi-age-years', { y })}</span>
                        <Pig name={i === 0 ? 'pig-pink' : 'pig-grey'} width={Math.round(34 + balance(y, rate) / 30)} />
                      </div>
                    </div>
                    <strong>${money(balance(y, rate))}</strong>
                    <em>{t('cmi-times', { n: times(balance(y, rate)) })}</em>
                  </li>
                );
              })}
            </ul>
            <p className="cmi-age-gap">
              {t('cmi-age-gap', {
                gap: `$${money(balance(END_AGE - START_AGES[0], rate) - balance(END_AGE - START_AGES[1], rate))}`,
                pct: Math.round(
                  (1 - balance(END_AGE - START_AGES[1], rate) / balance(END_AGE - START_AGES[0], rate)) * 100
                )
              })}
            </p>
            <p className="cmi-age-note">{t('cmi-age-note', { p: PRINCIPAL, rate })}</p>
          </div>
        )}
      </div>

      <p className="cmi-disclaimer">
        {t('dsc-disclaimer-1')}
        <br />
        {t('dsc-disclaimer-2')}
      </p>
    </div>
  );
};

export default CompoundInterest;
